import React, { useEffect, useState, useCallback, useRef } from 'react';
import API from '../../services/api';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { StatusBadge, Notice, apiErrors } from './formBits';
import PolicyForm from './PolicyForm';
import DiagnosisEditor from './DiagnosisEditor';
import { coverageStatus, authorizationWarning, relationshipLabel, policyHeadline } from '../../insurance/policyDisplay';
import { formatMoney } from '../../insurance/format';
import { formatLongDate } from '../../dateOnly';
import { todayEastern } from '../../dateUtils';

// Insurance on the client profile. Admin mode adds/edits policies and card
// images; therapist mode is read-only for policies. Both roles maintain the
// ordered diagnosis list. A 404 means "not your client" (or no such client):
// show one quiet line, never the data.

// onDiagnosesSaved(primaryDisplay | null) lets the host page refresh its legacy
// "Diagnosis Code" field after the structured list changes.
export default function ClientInsurancePanel({ clientId, mode, onDiagnosesSaved }) {
  const isAdmin = mode === 'admin';
  const [policies, setPolicies] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [payers, setPayers] = useState([]);
  const [editing, setEditing] = useState(null);   // null | 'new' | policy object
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const reqs = [API.get(`/billing/clients/${clientId}/policies`), API.get(`/billing/clients/${clientId}/diagnoses`)];
      if (isAdmin) reqs.push(API.get('/billing/payers'));
      const [p, d, pay] = await Promise.all(reqs);
      setPolicies(p.data); setDiagnoses(d.data); if (pay) setPayers(pay.data.filter((x) => x.is_active)); setUnavailable(false); setError('');
    } catch (err) {
      if (err.response && err.response.status === 404) { setUnavailable(true); setPolicies([]); setDiagnoses([]); }
      else setError(apiErrors(err).message);
    }
  }, [clientId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  if (!clientId) return null;
  if (unavailable) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Insurance details are not available for this client.</div>;
  if (error) return <Notice tone="bad">{error}</Notice>;
  if (policies === null) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading insurance…</div>;

  const today = todayEastern();
  const activePolicy = policies.find((p) => coverageStatus(p, today).tone === 'good');
  const saveDiagnoses = async (rows) => {
    const r = await API.put(`/billing/clients/${clientId}/diagnoses`, { diagnoses: rows });
    setDiagnoses(r.data);
    if (onDiagnosesSaved) onDiagnosesSaved(r.data.length ? r.data[0].display : null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <h4 style={{ margin: 0, color: 'var(--primary-color)' }}>Insurance policies</h4>
        {isAdmin && <Button variant="subtle" onClick={() => setEditing('new')} disabled={editing === 'new'}>Add policy</Button>}
      </div>
      {editing === 'new' && <PolicyForm clientId={clientId} payers={payers} initial={null} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {policies.length === 0 && editing !== 'new' && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>No insurance on file. {isAdmin ? 'Add a policy to start billing insurance for this client.' : 'The office adds policies.'}</div>}
      {policies.map((p) => (
        editing && editing.id === p.id
          ? <PolicyForm key={p.id} clientId={clientId} payers={payers} initial={p} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          : <PolicyCard key={p.id} policy={p} today={today} isAdmin={isAdmin} onEdit={() => setEditing(p)} onChanged={load} />
      ))}

      <h4 style={{ margin: '18px 0 6px', color: 'var(--primary-color)' }}>Diagnoses (ICD-10)</h4>
      {activePolicy && diagnoses.length === 0 && <Notice tone="warn">This client has active insurance but no diagnosis. Claims need at least one.</Notice>}
      <DiagnosisEditor diagnoses={diagnoses} canEdit onSave={saveDiagnoses} />
    </div>
  );
}

function PolicyCard({ policy: p, today, isAdmin, onEdit, onChanged }) {
  const status = coverageStatus(p, today);
  const authWarn = authorizationWarning(p);
  const [viewing, setViewing] = useState(null);     // { side, url, type }
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const frontInput = useRef(null), backInput = useRef(null);

  useEffect(() => () => { if (viewing) URL.revokeObjectURL(viewing.url); }, [viewing]);

  async function view(side) {
    setBusy(side); setMsg(null);
    try {
      const r = await API.get(`/billing/policies/${p.id}/card/${side}`, { responseType: 'blob' });
      if (viewing) URL.revokeObjectURL(viewing.url);
      setViewing({ side, url: URL.createObjectURL(r.data), type: r.headers['content-type'] || '' });
    } catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
    finally { setBusy(''); }
  }
  async function upload(side, file) {
    if (!file) return;
    setBusy(side); setMsg(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      await API.post(`/billing/policies/${p.id}/card/${side}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ tone: 'good', text: `Card ${side} saved.` }); onChanged();
    } catch (err) {
      const code = err.response && err.response.status;
      setMsg({ tone: 'bad', text: code === 413 ? 'That image is larger than 8 MB. Please use a smaller photo.' : apiErrors(err).message });
    } finally { setBusy(''); }
  }

  const line = (label, value) => (value ? <div><span style={{ color: 'var(--text-muted)' }}>{label}:</span> {value}</div> : null);

  return (
    <Card style={{ marginBottom: 10, border: '1px solid var(--border-color)', opacity: p.isActive ? 1 : 0.65 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--primary-color)' }}>{policyHeadline(p)}</div>
          <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            {p.authorizationSignedOn ? <StatusBadge tone="good">Authorization signed {formatLongDate(p.authorizationSignedOn)}</StatusBadge> : null}
          </div>
        </div>
        {isAdmin && <Button variant="ghost" style={{ padding: '6px 10px', fontSize: 12 }} onClick={onEdit}>Edit</Button>}
      </div>
      {authWarn && <Notice tone="warn">{authWarn}</Notice>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4px 16px', fontSize: 13, marginTop: 8 }}>
        {line('Group', p.groupNumber)}
        {line('Plan', p.planName)}
        {line('Effective', p.effectiveDate && formatLongDate(p.effectiveDate))}
        {line('Ends', p.terminationDate && formatLongDate(p.terminationDate))}
        {line('Copay', p.copayAmount !== null && p.copayAmount !== undefined ? formatMoney(p.copayAmount) : null)}
        {line('Coinsurance', p.coinsurancePercent !== null && p.coinsurancePercent !== undefined ? `${p.coinsurancePercent}%` : null)}
        {line('Deductible', p.deductibleNotes)}
        {line('Subscriber', p.relationship === 'self' ? 'Self' : `${relationshipLabel(p.relationship)} — ${[p.subscriberFirstName, p.subscriberLastName].filter(Boolean).join(' ')}${p.subscriberDob ? `, DOB ${formatLongDate(p.subscriberDob)}` : ''}`)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, fontSize: 13 }}>
        {['front', 'back'].map((side) => {
          const has = side === 'front' ? p.hasCardFront : p.hasCardBack;
          const ref = side === 'front' ? frontInput : backInput;
          return (
            <span key={side} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span style={{ color: 'var(--text-muted)' }}>Card {side}:</span>
              {has ? <Button variant="subtle" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => view(side)} disabled={busy === side}>{busy === side ? 'Loading…' : 'View'}</Button> : <span style={{ color: 'var(--text-muted)' }}>none</span>}
              {isAdmin && (
                <>
                  <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" style={{ display: 'none' }} aria-label={`Upload card ${side}`} onChange={(e) => { upload(side, e.target.files[0]); e.target.value = ''; }} />
                  <Button variant="ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => ref.current && ref.current.click()} disabled={busy === side}>{has ? 'Replace' : 'Upload'}</Button>
                </>
              )}
            </span>
          );
        })}
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {viewing && (
        <div style={{ marginTop: 10, border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, background: 'var(--surface-muted)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>Card {viewing.side}</span>
            <button type="button" onClick={() => { URL.revokeObjectURL(viewing.url); setViewing(null); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary-color)', fontWeight: 600 }}>Close</button>
          </div>
          {viewing.type.startsWith('image/')
            ? <img src={viewing.url} alt={`Insurance card ${viewing.side}`} style={{ maxWidth: '100%', maxHeight: 420, display: 'block', borderRadius: 4 }} />
            : <iframe title={`Insurance card ${viewing.side}`} src={viewing.url} style={{ width: '100%', height: 420, border: 'none' }} />}
        </div>
      )}
    </Card>
  );
}
